import React, { useState, useEffect } from 'react';
import { Table, DatePicker, Button, Select, Spin, Tooltip, Drawer } from 'antd';
import { useActivityTracking } from './store/index';

const { Option } = Select;

const IdeaDashboard = () => {
  const [data, setData] = useState([]); // Empty initially, will be filled from API
  const [filters, setFilters] = useState([]); // This will store the filters coming from the API
  const [selectedFilters, setSelectedFilters] = useState({}); // This stores the user-selected filters
  const [drawerVisible, setDrawerVisible] = useState(false); // State to manage drawer visibility
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [pageSize, setPageSize] = useState(10); // Page size state
  const { fetchActivitySummary, fetchActivityTrackingFilters, activitySummaryLoading, filtersLoading } = useActivityTracking();

  // Fetch filters from API when component mounts and apply default filter
  useEffect(() => {
    const fetchFilters = async () => {
      await fetchActivityTrackingFilters(); // Fetching filters from the API
      const response = useActivityTracking.getState().filters; // Access the filters
      setFilters(response.filters || []);

      // Automatically apply default filters after fetching
      await handleFilter({ pageNumber: currentPage, pageSize });
    };
    fetchFilters();
  }, []);

  // Update selected filters state
  const handleFilterChange = (filterCode, value) => {
    setSelectedFilters({
      ...selectedFilters,
      [filterCode]: value,
    });
  };

  // Function to apply filters and call the API
  const handleFilter = async (paginationConfig) => {
    const { pageNumber, pageSize } = paginationConfig || { pageNumber: currentPage, pageSize: pageSize };

    // Call the API to fetch activity summary with selected filters and pagination
    await fetchActivitySummary({ ...selectedFilters, pageNumber, pageSize });

    // Assuming the API response is set in the `activitySummary` state in the store
    const result = useActivityTracking.getState().activitySummary;

    // Transform the result into the structure expected by the table
    const transformedData = result.map((item, index) => ({
      key: index + 1,
      coachName: item.userName,
      firstLoginTime: item.loginTime,
      lastLogoutTime: item.logoutTime,
      offlineTime: item.totalOfflineHours,
      idleTime: item.totalIdleHours,
      onlineTime: item.totalOnlineHours,
      movTime: item.totalMovementHours,
      onlineLogs: item.onlineLogs,
      movementLogs: item.movementLogs,
    }));

    setData(transformedData);
    setDrawerVisible(false); // Close the drawer after applying filters
  };

  // Handle pagination and table changes
  const handleTableChange = (pagination) => {
    const { current, pageSize } = pagination;
    setCurrentPage(current);
    setPageSize(pageSize);
    handleFilter({ pageNumber: current, pageSize }); // Ensure pagination is passed
  };

  const columns = [
    {
      title: (
        <Tooltip title="The name of the coach being tracked.">
          Coach Name
        </Tooltip>
      ),
      dataIndex: 'coachName',
      key: 'coachName',
    },
    {
      title: (
        <Tooltip title="Time when the coach first logged in for the day.">
         First Login Time
        </Tooltip>
      ),
      dataIndex: 'firstLoginTime',
      key: 'firstLoginTime',
    },
    {
      title: (
        <Tooltip title="Time when the coach last logged out for the day.">
          Last Logout Time
        </Tooltip>
      ),
      dataIndex: 'lastLogoutTime',
      key: 'lastLogoutTime',
    },
    {
      title: (
        <Tooltip title="The total time the coach was offline between login and logout.">
          Total Offline Time
        </Tooltip>
      ),
      dataIndex: 'offlineTime',
      key: 'offlineTime',
    },
    {
      title: (
        <Tooltip title="Total time the coach spent online during the day.">
          Total Online Time
        </Tooltip>
      ),
      dataIndex: 'onlineTime',
      key: 'onlineTime',
      render: (text, record) => (
        <Tooltip title={renderLogTooltip(record.onlineLogs)}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
    {
      title: (
        <Tooltip title="Total idle time when the coach was online but not active.">
          Total Idle Time
        </Tooltip>
      ),
      dataIndex: 'idleTime',
      key: 'idleTime',
    },
    {
      title: (
        <Tooltip title="Total time spent in movement or active work by the coach.">
          Total Movement Time
        </Tooltip>
      ),
      dataIndex: 'movTime',
      key: 'movTime',
      render: (text, record) => (
        <Tooltip title={renderLogTooltip(record.movementLogs)}>
          <span>{text}</span>
        </Tooltip>
      ),
    },
  ];
  
  // Dynamically render the filter components
  const renderFilters = () => {
    return filters.map((filter) => {
      switch (filter.filter_type) {
        case 'single_date_picker':
          return (
            <div key={filter.filter_code} style={{ marginBottom: 16 }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#5f6368' }}>{filter.filter_title}:</label>
              <DatePicker
                style={{ width: '100%', padding: '8px', borderRadius: '6px', backgroundColor: 'white' }}
                onChange={(date, dateString) =>
                  handleFilterChange(filter.filter_code, dateString)
                }
              />
            </div>
          );

        case 'code_name_spinner':
          return (
            <div key={filter.filter_code} style={{ marginBottom: 16 }}>
              <label style={{ fontSize: '14px', fontWeight: '600', color: '#5f6368' }}>{filter.filter_title}:</label>
              <Select
                style={{ width: '100%', fontSize: '14px', padding: '8px', borderRadius: '6px', backgroundColor: 'white' }}
                mode={filter.is_single_select ? 'single' : 'multiple'}
                onChange={(value) => handleFilterChange(filter.filter_code, value)}
                placeholder={`Select ${filter.filter_title}`}
              >
                {filter.filter_values.map((val) => (
                  <Option key={val.code} value={val.code}>
                    {val.value}
                  </Option>
                ))}
              </Select>
            </div>
          );

        default:
          return null;
      }
    });
  };

  // Function to render the logs as tooltips for online and movement times
  const renderLogTooltip = (logs) => (
    logs.length > 0 ? logs.map((log, index) => <div key={index}>{log}</div>) : 'No logs available'
  );

  return (
    <div>
      <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px' }}>Productivity Tracker Dashboard</h2>

      <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between"}}>
        <div>
          <Button
            style={{ marginRight: 16 }}
            onClick={() => setDrawerVisible(true)} // Open the filter drawer
            type="default"
          >
            Filter
          </Button>
          <Button
            style={{ marginRight: 16 }}
            type="primary"
            onClick={() => handleFilter({ pageNumber: currentPage, pageSize })}
            loading={activitySummaryLoading}
          >
            {activitySummaryLoading ? 'Loading...' : 'Apply Filters'}
          </Button>
        </div>
        <span className="shadow p-2 text-nowrap text-center">
          Results: {data.length}
        </span>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={activitySummaryLoading}
        onChange={handleTableChange}
        pagination={{
          current: currentPage, // Current page state
          pageSize: pageSize,   // Page size state
          total: useActivityTracking.getState().totalRecords || 0,  // Dynamically update with actual total records from the API
          showSizeChanger: true // Allow the user to change page size
        }}
      />

      <Drawer
        title={<span style={{ fontSize: '18px', fontWeight: 'bold' }}>Filter Options</span>}
        placement="right"
        closable={true}
        onClose={() => setDrawerVisible(false)}
        visible={drawerVisible}
        width={350}
        zIndex={100000}
      >
        {filtersLoading ? <Spin /> : renderFilters()}
        <Button
          type="primary"
          style={{ marginTop: 16, width: '100%', fontSize: '14px', fontWeight: 'bold', padding: '10px 0' }}
          onClick={() => handleFilter({ pageNumber: currentPage, pageSize })}
        >
          Apply Filters
        </Button>
      </Drawer>
    </div>
  );
};

export default IdeaDashboard;
